// NdsCoverBlock — the calc-report cover / identity + engineer stamp block.
//
// Renders the ClearCalcs-style page-1 header: calc type + member mark, the
// project identity fields (Client / Engineer / Job # / Address / code …), the
// code references, and a sign-off / stamp block. Every field is sourced from
// the persisted project report_* columns or the engine result — nothing is
// fabricated, and a blank field is simply omitted.
import type { CalcProject } from "../../types/calc";

// Calculator display titles for the cover line — mirrors the registry `name`
// of every design calculator (profiles/calculators/calculator_registry_v1.json).
// Load calculators render on their own workspace and don't use this block.
const CALC_TITLES: Record<string, string> = {
  wood_beam_asd: "Wood Beam",
  wood_column_asd: "Wood Column",
  wood_tension_asd: "Wood Tension / Roof Tie",
  wood_stud_wall_asd: "Wood Stud Wall",
  wood_scl_beam_asd: "SCL Beam (LVL / LSL / PSL)",
  wood_biaxial_beam_column_asd: "Wood Biaxial Beam-Column",
  glulam_beam_asd: "Glulam Beam",
  wood_ijoist_asd: "Wood I-Joist",
  wood_nail_connection_asd: "Wood Nail / Spike Connection",
  wood_bolt_connection_asd: "Wood Bolt Connection",
  wood_lag_connection_asd: "Wood Lag-Screw Connection",
  wood_screw_connection_asd: "Wood-Screw Connection",
  connector_product_capacity: "Connector Product Capacity",
  steel_beam_aisc: "Steel Beam",
  steel_column_aisc: "Steel Column",
  cfs_joist: "Cold-Formed Steel Joist",
  cfs_stud_wall: "Cold-Formed Steel Stud Wall",
  cfs_header: "Cold-Formed Steel Header",
  concrete_beam_aci: "Concrete Beam",
  continuous_footing: "Strip / Continuous Footing",
  pad_footing: "Pad Footing",
  masonry_bearing_wall_tms: "Masonry Out-of-Plane Wall",
  masonry_shear_wall_tms: "Masonry Shear Wall",
  masonry_lintel_tms: "Masonry Lintel",
  proprietary_hanger_connection: "Proprietary Hanger Capacity Check",
  proprietary_hold_down_connection: "Proprietary Hold-Down Load Path",
  section_properties: "Section Properties",
  load_wind_mwfrs: "Wind — MWFRS",
  load_wind_cc: "Wind — Components and Cladding",
  load_snow: "Snow — Balanced, Unbalanced, and Drift",
  load_seismic_elf: "Seismic — Equivalent Lateral Force",
  load_dead_gravity: "Dead and Gravity Loads",
  load_roof_live: "Roof Live Load",
  load_floor_deck_live: "Floor and Deck Live Loads",
  load_ceiling_attic_live: "Ceiling and Attic Live Loads",
  load_rain_ice: "Rain and Ice Loads",
  load_wall_gravity: "Wall Gravity Load",
};

// Format an ISO timestamp to a date — engine/server authored, never invented.
function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function NdsCoverBlock({
  project,
  calculatorId,
  memberMark,
  roleLabel,
  edition,
  designMethod,
  updatedAt,
  peStamped,
}: {
  project: CalcProject | null;
  calculatorId: string;
  memberMark: string;
  roleLabel: string | null;
  edition: string | null;
  // null = the calculator is method-fixed (ACI strength, TMS, published
  // allowables …) — printing ASD/LRFD there would be a fabricated attribute.
  designMethod: "ASD" | "LRFD" | null;
  updatedAt: string | null;
  peStamped: boolean;
}) {
  // Edition is engine-authored provenance (registry-declared standard as the
  // schema-sheet fallback); the paren carries only what is real — never a
  // guessed standard, never a method the calculator doesn't offer.
  const parenthetical = [edition, designMethod].filter(Boolean).join(" ");
  const calcTitle = `${CALC_TITLES[calculatorId] ?? "Structural Member"}${
    parenthetical ? ` (${parenthetical})` : ""
  }`;
  const refs = [
    edition ? edition.replace("NDS-", "NDS ") : null,
    designMethod,
    // The engine's load-combination basis (engine.member_check.combos builds
    // the ASCE 7-22 §2.3/§2.4 sets). Surface from the structured payload once
    // the engine exports it; until then this names the real basis, not a guess.
    "ASCE 7-22",
    project?.reportBuildingCode || null,
  ]
    .filter(Boolean)
    .join(" · ");
  const date = fmtDate(updatedAt);

  const rows: { k: string; v: string | null }[] = [
    { k: "Project", v: project?.name ?? null },
    { k: "Client", v: project?.reportClient ?? null },
    { k: "Job #", v: project?.reportJobNumber ?? null },
    { k: "Address", v: project?.address ?? null },
    { k: "Engineer", v: project?.reportEngineer ?? null },
    { k: "License", v: project?.reportLicense ?? null },
    { k: "Building code", v: project?.reportBuildingCode ?? null },
    { k: "Risk category", v: project?.reportRiskCategory ?? null },
    { k: "Date", v: date },
    { k: "References", v: refs || null },
  ].filter((r): r is { k: string; v: string } => !!r.v);

  return (
    <div className="nds-cover">
      <div className="nds-cover-head">
        <div className="nds-cover-titles">
          <div className="nds-cover-calc">
            {calcTitle}
            {roleLabel ? <span className="nds-cover-role"> · {roleLabel}</span> : null}
          </div>
          <div className="nds-cover-mark">{memberMark || "—"}</div>
        </div>
        <div className={`nds-cover-stamp${peStamped ? " is-stamped" : ""}`}>
          <div className="nds-cover-stamp-label">
            {peStamped ? "Checked / sealed by" : "Engineer of record"}
          </div>
          <div className="nds-cover-stamp-eng">
            {project?.reportEngineer || "—"}
          </div>
          {project?.reportLicense ? (
            <div className="num nds-cover-stamp-lic">
              License {project.reportLicense}
            </div>
          ) : null}
          {date ? <div className="num nds-cover-stamp-date">{date}</div> : null}
        </div>
      </div>
      {rows.length > 0 ? (
        <div className="nds-cover-grid">
          {rows.map((r) => (
            <div className="nds-cover-row" key={r.k}>
              <span className="nds-cover-k">{r.k}</span>
              <span className="nds-cover-v">{r.v}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
